import mfac from '../material-factory';
import getLogger from '../logging';
import { Color, ColorRepresentation, MeshStandardMaterial } from 'three';

const log = getLogger('view3d');

const isString = (x: any): x is String => {
    return (typeof x) == "string";
}

export const overrideMaterialFactory = () => {
    const loader = mfac.gltfMaterialLoader;

// The MaterialFactory's extension map keys.
// ['Brass', 'Bronze', 'Carpet', 'Chrome', 'FrostedGlass', 'GenericAsset', 'GenericMatte', 'Glass', 'GlossBlack', 'GlossMelamine', 'Herringbone', 'Ivory', 'LeatherOyster', 'LeatherPewter', 'LeatherWinter', 'LightCool', 'LightNeutral', 'LightWarm', 'MatteAluminum', 'MatteBlack', 'MatteGold', 'Mirror', 'Nickel', 'OriginalFlooring', 'Parquet', 'SatinNickel', 'SemiGlossMelamine', 'Slate', 'StandardMelamine', 'WallExtension', 'WallPaint', 'White', 'WindowEnvironment']

    mfac.override('Brass', () => loader.load({assetId: 'materials/brass'}));
    mfac.override('Bronze', () => loader.load({assetId: 'materials/bronze'}));
    mfac.override('Carpet', () => loader.load({assetId: 'materials/carpet'}));
    mfac.override('Chrome', () => loader.load({assetId: 'materials/chrome'}));
    mfac.override('FrostedGlass', () => loader.load({assetId: 'materials/frosted-glass'}));
    mfac.override('Glass', () => loader.load({assetId: 'materials/glass'}));
    mfac.override('GlossBlack', () => loader.load({assetId: 'materials/gloss-black'}));
    mfac.override('GlossMelamine', () => loader.load({assetId: 'materials/gloss-melamine'}));
    mfac.override('Herringbone', (colorcode: string) => {
        switch(colorcode) {
            case '01A':
                return loader.load({ assetId: 'materials/floor-herringbone-01' });
            case '02A':
                return loader.load({ assetId: 'materials/floor-herringbone-02' });
            case '02B':
                return loader.load({ assetId: 'materials/floor-herringbone-03' });
            case '02C':
                return loader.load({ assetId: 'materials/floor-herringbone-04' });
            case '03A':
                return loader.load({ assetId: 'materials/floor-herringbone-05' });
            default:
                log.debug(`Unrecognized Herringbone colormap ${colorcode}`);
                return new MeshStandardMaterial();
        }
    });
    mfac.override('Ivory', () => loader.load({assetId: 'materials/ivory'}));
    mfac.override('LeatherOyster', () => loader.load({assetId: 'materials/leather-oyster'}));
    mfac.override('LeatherPewter', () => loader.load({assetId: 'materials/leather-pewter'}));
    mfac.override('LeatherWinter', () => loader.load({assetId: 'materials/leather-winter'}));
    mfac.override('LightCool', () => loader.load({assetId: 'materials/light-cool'}));
    mfac.override('LightNeutral', () => loader.load({assetId: 'materials/light-neutral'}));
    mfac.override('LightWarm', () => loader.load({assetId: 'materials/light-warm'}));
    mfac.override('MatteAluminum', () => loader.load({assetId: 'materials/matte-aluminum'}));
    mfac.override('MatteBlack', () => loader.load({assetId: 'materials/matte-black'}));
    mfac.override('MatteGold', () => loader.load({assetId: 'materials/matte-gold'}));
    mfac.override('Mirror', () => loader.load({assetId: 'materials/mirror'}));
    mfac.override('Nickel', () => loader.load({assetId: 'materials/satin-nickel'}));
    mfac.override('OriginalFlooring',
                  (colorcode: string) => loader.load(
                      {assetId: `materials/floor-wood-${colorcode}`}));
    mfac.override('Parquet', () => loader.load({assetId: 'materials/floor-parquet-01'}));
    mfac.override('SatinNickel', () => loader.load({assetId: 'materials/satin-nickel'}));
    mfac.override('SemiGlossMelamine',
                  () => loader.load({assetId: 'materials/semi-gloss-melamine'}));
    mfac.override('Slate', () => loader.load({assetId: 'materials/slate'}));
    mfac.override('StandardMelamine', (colormap: string) => {
        let color: string = undefined;
        if(colormap) {
            const match = colormap.match(
                /closet-organizers-images\/models\/colors\/(\d+)\.png$/);
            if(match) {
                // Transform the path to the assetId format for the colormap
                color = `color-maps/${match[1]}`;
            }
        }
        return loader.load({
            assetId: 'materials/standard-melamine',
            color,
        });
    });

    // WallExtension is a little weird and it's already MeshStandardMaterial,
    // so I'm omitting it temporarily.
    mfac.override('WallPaint', (wallColor: ColorRepresentation) => {
        if(isString(wallColor) && !wallColor.startsWith('#')) {
            wallColor = '#' + wallColor;
        }
        const tint = new Color(wallColor ?? '#B0B0B0');
        return loader.load({
            assetId: 'materials/wall-paint',
            tint: `#${tint.getHexString()}`,
        })
    });

    mfac.override('White', () => loader.load( { assetId: 'materials/white' }));
    mfac.override('WindowEnvironment', () => loader.load(
        { assetId: 'materials/window-environment-01' }));

    mfac.override('ShadelessColor', (tint) => loader.load(
        { assetId: 'materials/shadeless-color', tint: String(tint) ?? '#000000' } ));

};
